import { ResourceDWR } from "../../../dto/ResourceDWR";
import { LoaderFunctionArgs, redirect, useLoaderData } from "react-router-dom";
import { LoaderData } from "../account/MyAccount";
import { BackToLink } from "./BackToLink";
import { Box, Button, Container, Toolbar } from "@mui/material";
import React from "react";
import { TitlePage } from "../title/TitlePage";
import { NewsFeedLayout } from "../news/NewsFeedLayout";

import {
  LocationConfigurationHolder,
  TitleTab,
} from "../../LocationConfiguration";
import { tabToRouteMap } from "../../TabUtils";
import { useIsMobile } from "@tlc/ui/useIsMobile";
import { useTitleNavigation } from "../../nav/TitleNavigationProvider";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { useLanguage } from "../../Language";
import { getResourceByHostRecordId, getResourceById } from "./getResourceById";
import { SyndeticsConfigHolder } from "../../SyndeticsConfig";

export async function getRedirectToDefaultTab(data: ResourceDWR) {
  const locationConfiguration =
    await LocationConfigurationHolder.fetchLocationConfiguration();
  const { useNoveList, displaySettings } =
    locationConfiguration.pacSettings ?? {};
  const { showUserReviewsView } = displaySettings ?? {};
  const tabSetups = locationConfiguration.pacSettings.titleDetailsTabSetups;
  const syndeticsData = await SyndeticsConfigHolder.getData();
  const availableTabs = tabSetups.filter((tabsetup) => {
    const tabLabel = tabsetup.label;
    switch (tabLabel) {
      case TitleTab.Details:
        return true;
      case TitleTab.AllCopies:
        return !data.downloadable;
      case TitleTab.Reviews:
        return showUserReviewsView || syndeticsData.hasReviewsTabEnrichments;
      case TitleTab.Related:
        return useNoveList || syndeticsData.hasRelatedTabEnrichments;
      case TitleTab.MoreInfo:
        return syndeticsData.hasMoreInfoTabEnrichments;
      default:
        return false;
    }
  });
  const defaultTab = availableTabs.find((tab) => tab.defaultTab);
  if (defaultTab) {
    const path = tabToRouteMap[defaultTab.label];
    return path;
  }
  const firstAvailablePath = tabToRouteMap[availableTabs[0].label];
  return firstAvailablePath;
}

export async function titleIndexLoader({ params }: LoaderFunctionArgs) {
  const titleId = params.titleId;
  const hostRecordId = params.hostRecordId;
  const data = !!titleId
    ? await getResourceById(titleId)
    : await getResourceByHostRecordId(hostRecordId);

  const basePath = !!titleId
    ? `/resource/${titleId}`
    : `/hostrecord/${hostRecordId}`;

  if (data.rtype !== 1) {
    return redirect(`${basePath}/details`);
  }
  const defaultTabPath = await getRedirectToDefaultTab(data);
  return redirect(`${basePath}/${defaultTabPath}`);
}

export async function resourcePageLoader({ params }: LoaderFunctionArgs) {
  const resourceId = params.titleId;
  const data = await getResourceById(resourceId);
  return { resource: data };
}

export const ResourcePage: React.FC = () => {
  const { translate } = useLanguage();
  const { resource } = useLoaderData() as LoaderData<typeof resourcePageLoader>;
  const { prev, next } = useTitleNavigation();
  const isMobile = useIsMobile();

  //Snap the user to the top of title details on first load
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  return (
    <Container>
      <BackToLink></BackToLink>
      {prev && next && (
        <Toolbar disableGutters>
          <Button
            onClick={prev.onClick}
            disabled={prev.disabled}
            startIcon={<NavigateBeforeIcon />}
          >
            {translate("prev")}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={next.onClick}
            disabled={next.disabled}
            endIcon={<NavigateNextIcon />}
          >
            {translate("next")}
          </Button>
        </Toolbar>
      )}

      {resource.rtype === 1 && <TitlePage resource={resource} />}

      {resource.rtype !== 1 && <NewsFeedLayout resource={resource} />}
    </Container>
  );
};
